/**
 * ATL-1445 stage 3, P6 — the pure folds of the local view over the server state (plan §3).
 *
 * Each function takes the server value and one surface's overlay and returns what the device shows.
 * With a `null` overlay each returns its server argument ITSELF (`===`): that is the today-path
 * guarantee, pinned by `localViewDifferential.test.js` and the render-count test.
 *
 * Semantics are Firestore mode's, because that is what a till showed before its read cutover and
 * what a Firestore-mode till shows today for its own unsent writes (latency compensation):
 *  - stock: `increment` — a numeric current value is added to; anything else (absent, `null`, a
 *    string) is REPLACED by the delta, which is what the server transform does;
 *  - totals: the same, field by field; `assign` fields are assigned, then `remove` fields deleted;
 *  - records: `create` adds a row only where the server has none (the server's row wins — it is the
 *    acknowledged truth, e.g. after a lost acknowledgement); `patch` merges over whatever is shown;
 *    `delete` hides it.
 * Every operation is folded in FIFO order, records and stock interleaved per product, so a patch that
 * sets a stock level and a later sale compose the way the batches did.
 */
import type { DailyStat, DailyStatsDict, ProductsDict } from '../AtomsProvider.types'
import type { DailyStatsOverlay, PendingRow, ProductOverlayOp, ProductsOverlay } from './localViewState'

type ProductValue = ProductsDict[string]

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

/** Firestore `increment`: numeric + delta, anything else becomes the delta. */
export function incrementLikeFirestore(current: unknown, delta: number): number {
  return typeof current === 'number' ? current + delta : delta
}

/**
 * Per overlaid product, the last fold: the same server object under the same ops gives the same result
 * OBJECT, so a server write that did not touch an overlaid product does not hand its row a new object
 * (reviewer N2: every overlaid row would re-render on every unrelated write). Keyed weakly by the ops
 * array, which the engine replaces whenever that product's overlay changes.
 */
const foldMemo = new WeakMap<readonly ProductOverlayOp[], { base: ProductValue | undefined; result: ProductValue | undefined; touched: boolean }>()

/** The products dict the device shows: server + this device's pending product writes. */
export function applyProductsOverlay(server: ProductsDict, overlay: ProductsOverlay | null): ProductsDict {
  if (overlay === null) return server
  const result: ProductsDict = { ...server }
  for (const [key, ops] of overlay) {
    const base: ProductValue | undefined = Object.prototype.hasOwnProperty.call(server, key) ? server[key] : undefined
    const memo = foldMemo.get(ops)
    if (memo && memo.base === base) {
      if (!memo.touched) continue
      if (memo.result === undefined) delete result[key]
      else result[key] = memo.result
      continue
    }
    let current: ProductValue | undefined = base
    let touched = false
    let stockCopied = false
    for (const op of ops) {
      if (op.kind === 'record') {
        if (op.op === 'create') {
          if (current !== undefined) continue // the server's row wins
          current = { ...(op.fields as Record<string, unknown>), id: key } as ProductValue
        } else if (op.op === 'patch') {
          if (current === undefined) continue
          current = { ...current, ...(op.fields as Record<string, unknown>) } as ProductValue
        } else {
          current = undefined
        }
        touched = true
        stockCopied = false
        continue
      }
      if (current === undefined) continue // a product the device cannot see takes no stock
      if (!stockCopied) {
        const storeStock = isPlainRecord(current.storeStock) ? { ...current.storeStock } : {}
        current = { ...current, storeStock } as ProductValue
        stockCopied = true
      }
      const storeStock = current.storeStock as Record<string, unknown>
      storeStock[op.storeId] = incrementLikeFirestore(storeStock[op.storeId], op.delta)
      touched = true
    }
    foldMemo.set(ops, { base, result: current, touched })
    if (!touched) continue
    if (current === undefined) delete result[key]
    else result[key] = current
  }
  return result
}

/**
 * The business day (`YYYY-MM-DD`) a server `dailyStats` entry is for, in either read mode:
 *  - Supabase: `businessDate`, else an id that is already `YYYY-MM-DD` (`AppStack` keys the entry by
 *    the response's `businessDate || date`);
 *  - Firestore: the document id `d-M-y-<store>` (`getDailyStatID`).
 * `null` when it cannot be told; the overlay then adds nothing to that entry (it cannot prove the
 * pending write is for the day the entry shows).
 */
export function serverStatBusinessDay(entry: DailyStat | undefined): string | null {
  if (!entry) return null
  const businessDate = (entry as { businessDate?: unknown }).businessDate
  if (typeof businessDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(businessDate)) return businessDate
  const id = entry.id
  if (typeof id !== 'string') return null
  if (/^\d{4}-\d{2}-\d{2}$/.test(id)) return id
  const firestore = /^(\d{1,2})-(\d{1,2})-(\d{4})-./.exec(id)
  if (!firestore) return null
  const [, d, m, y] = firestore
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
}

/**
 * Today's totals the device shows: server + this device's pending writes, for the day the entry is.
 *
 * Which entry an op lands on, per store:
 *  - the server's entry is for today (or a later day, or today cannot be computed): ops booked to THAT
 *    day add to it;
 *  - there is no server entry, or the server's entry is for an EARLIER day (`subscribeToDailyStats`
 *    writes only the stores today's read returned, so yesterday's entry stays until the store's first
 *    row of the day): ops booked to TODAY start a fresh entry — what Firestore's listener on today's
 *    document shows the moment the device writes it (a shift opened before the day's first sale,
 *    invariant 11);
 *  - a server entry whose day cannot be read is left alone (nothing can prove the op is its day).
 */
export function applyDailyStatsOverlay(server: DailyStatsDict, overlay: DailyStatsOverlay | null): DailyStatsDict {
  if (overlay === null) return server
  let result: DailyStatsDict | null = null
  for (const [storeId, ops] of overlay.byStore) {
    const entry = Object.prototype.hasOwnProperty.call(server, storeId) ? server[storeId] : undefined
    const today = overlay.todayByStore.get(storeId) ?? null
    const serverDay = entry ? serverStatBusinessDay(entry) : null
    if (entry && serverDay === null) continue
    const stale = !entry || (serverDay !== null && today !== null && serverDay < today)
    const day = stale ? today : serverDay
    if (day === null) continue
    let current: Record<string, unknown> | null = null
    for (const op of ops) {
      if (op.businessDay !== day) continue
      if (current === null) current = entry && !stale ? { ...entry } : { id: op.dailyStatId, store: storeId }
      for (const [field, delta] of Object.entries(op.increments)) current[field] = incrementLikeFirestore(current[field], delta)
      for (const [field, value] of Object.entries(op.assign)) current[field] = value
      for (const field of op.remove) delete current[field]
    }
    if (current === null) continue
    if (result === null) result = { ...server }
    result[storeId] = current as DailyStat
  }
  // An overlay that applied to no entry (another day, an unknown day) leaves the server object as is.
  return result ?? server
}

// ============================================================================
// Rows
// ============================================================================

/** Whether a pending row passes a list's store and instant-range filters. */
export function pendingRowInView(
  row: Pick<PendingRow, 'store' | 'date'>,
  view: { store: string; startMs: number | null; endMs: number | null },
): boolean {
  if (view.store !== 'all' && row.store !== view.store) return false
  if (view.startMs === null && view.endMs === null) return true
  if (row.date === null) return false
  const at = Date.parse(row.date)
  if (!Number.isFinite(at)) return false
  if (view.startMs !== null && at < view.startMs) return false
  if (view.endMs !== null && at > view.endMs) return false
  return true
}

/** Ids of the rows a list shows (server rows), for de-duplication and for retiring acked rows. */
export function idsOfRows(rows: Iterable<{ id?: unknown; firestoreId?: unknown; firestore_id?: unknown }>): Set<string> {
  const ids = new Set<string>()
  for (const row of rows) {
    for (const candidate of [row.id, row.firestoreId, row.firestore_id]) {
      if (typeof candidate === 'string' && candidate.length > 0) ids.add(candidate)
    }
  }
  return ids
}
